import {
  BridgeDebugger,
  FIELD_DIRECTORY,
  Feature,
  FieldValidators,
  FormErrors,
  RequestTransport,
  WidgetContextProvider,
  getAddressAttributes,
  useAllForms,
  useBridgeContext,
  useEntityId,
  useGemsClientSetup,
  useGeoStudioGamClient,
  useGeoStudioGamSetup,
  useRequestTransport,
} from "@amzn/geo-studio-web-sdk";
import {
  Box,
  Button,
  Column,
  Input,
  Row,
  Select,
  SelectOption,
  Text,
  Toggle,
} from "@amzn/meridian";
import { useCallback, useState } from "react";
import ChangePreviewModal from "../ChangePreviewModal";

export const sampleValidators: FieldValidators = {
  onChange: ({ value }) => {
    const errors: FormErrors = {};

    // single-field validation
    if (value.deliveryPoint?.coordinates.latitude === 10) {
      errors.deliveryPoint = ["Latitude cannot be 10"];
    }

    // cross-field validation
    if (
      value.deliveryHint === "not residential" &&
      value.addressType?.toLowerCase() === "residential"
    ) {
      errors.generalErrors = ["Address type and delivery hint are conflicting"];
    }

    return Object.keys(errors).length > 0 ? errors : undefined;
  },
};

const SampleComponent = () => {
  // TODO: Remove hard-coded values, receive values from host
  useGeoStudioGamSetup(
    "https://api-gam.na.prod.geostudio.last-mile.amazon.dev/api",
    RequestTransport.MIDWAY_FETCH
  );

  // TODO: different auth methods affect link -- merge a change for different auths once multi-path-gateway is final
  useGemsClientSetup(
    "https://api.alpha.geo-edit-management.last-mile.amazon.dev/midway",
    RequestTransport.MIDWAY_FETCH
  );

  const gamClient = useGeoStudioGamClient();

  const [fieldNames, setFieldNames] = useState<(keyof typeof FIELD_DIRECTORY)[]>(["addressType"]);
  const [input, setInput] = useState("");
  const [entityId, setEntityId] = useEntityId();
  const { allForms, allFormValidities } = useAllForms();
  const [transportType, setTransportType] = useRequestTransport();
  const {
    useDebuggerState: [debuggerState, setDebuggerState],
  } = useBridgeContext();
  const [isChangePreviewModalOpen, setIsChangePreviewModalOpen] = useState(false);

  const searchEntity = () => {
    setEntityId(input);
    setInput("");
  };

  const getDefaultValues = useCallback(
    (entityId: string) => {
      if (!gamClient) return Promise.reject(new Error("GAM client is not defined"));
      return getAddressAttributes(gamClient, entityId);
    },
    [gamClient]
  );

  return (
    <Row width="100%" widths="fill">
      {debuggerState ? <BridgeDebugger /> : <></>}
      <Box spacingInset="400">
        <Column>
          <Select label="Select field(s)" onChange={setFieldNames} value={fieldNames}>
            {Object.keys(FIELD_DIRECTORY).map((fieldName) => (
              <SelectOption key={fieldName} value={fieldName} label={fieldName} />
            ))}
          </Select>
          <Select label="Select transport type" onChange={setTransportType} value={transportType}>
            {Object.entries(RequestTransport).map(([transportKey, val]) => (
              <SelectOption key={transportKey} value={val} label={transportKey} />
            ))}
          </Select>
          <Text>{`Debugger state: ${debuggerState ? "ON" : "OFF"}`}</Text>
          <Toggle checked={debuggerState} onChange={setDebuggerState} value={debuggerState} />

          <Row>
            <Input
              value={input}
              onChange={setInput}
              placeholder="Enter AID"
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === "Enter" && input) {
                  searchEntity();
                }
              }}
            />
            <Button onClick={searchEntity} disabled={!input}>
              Search
            </Button>
          </Row>

          <Select
            data-testid="entity-select"
            label="View local edits"
            helperText={entityId && `Currently viewing entityId: ${entityId}`}
            disabled={Object.keys(allForms).length === 0}
            onChange={setEntityId}
            placeholder="Select another entityId"
            value={entityId}
          >
            {Object.keys(allForms).map((id) => (
              <SelectOption key={id} label={id} value={id}>
                {() => <Text>{allFormValidities[id] === false ? `⚠️ ${id}` : id}</Text>}
              </SelectOption>
            ))}
          </Select>

          <WidgetContextProvider getDefaultValues={getDefaultValues} validators={sampleValidators}>
            <Column>
              <ChangePreviewModal
                isChangePreviewModalOpen={isChangePreviewModalOpen}
                setIsChangePreviewModalOpen={setIsChangePreviewModalOpen}
              />
              <Button
                type="secondary"
                onClick={() => setIsChangePreviewModalOpen(!isChangePreviewModalOpen)}
              >
                Show all changes
              </Button>
              {fieldNames.map((fieldName) => (
                <Feature key={fieldName} name={fieldName} />
              ))}
            </Column>
          </WidgetContextProvider>
        </Column>
      </Box>
    </Row>
  );
};

export default SampleComponent;
